/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { getAllRealmAction } from '../realm/realmActionCreator';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../../shared/constants';
import { setSnackBarDisplay } from '../snackBar/snackBarActionCreator';
import { showErrorAction, clearErrorAction } from "../error/errorActionCreator";
import { hideLoaderAction, showLoaderAction } from "../loader/loaderActionCreator";
import { getAllSubEnterpriseAction, setEntitlementsForSubEnterprise } from './subEnterpriseActionCreator';
import {
  getSubEnterPrisePayload,
  ICreateAccountRequest,
  IDeleteSubEnterpriseRequest,
  IGetSubEnterpriseEntitlement,
  IUpdateEntitlementRequest,
} from '../../models/Customers/subEnterprise';
import {
  deleteSubEnterprise,
  updateAccount,
  getAllSubEnterprises,
  createNewAccount,
  getAllEntitlementForSubEnterprise,
  updateEntitlementsForSubEnterprise,
} from '../../services/subEnterprise';
import { getValidationErrorMessages } from '../../utility/commonUtility';

export const getSubEnterprise = async (dispatch: Function, subEnterprisePayload: getSubEnterPrisePayload) => {
  let subEnterprise: any = [];
  let selectedSubEnterprise: any = {};
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const subEnterpriseResponse = await getAllSubEnterprises(subEnterprisePayload);
    if (subEnterpriseResponse.data) {
      subEnterprise = subEnterpriseResponse.data;
      selectedSubEnterprise = subEnterprise[0] || {};
      dispatch(getAllSubEnterpriseAction(subEnterprise, selectedSubEnterprise, subEnterprise.length > 0 ? false : true));
    }
    dispatch(hideLoaderAction());
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in Get Account: ' + errorMessageDetail
    };
    subEnterprise = [];
    selectedSubEnterprise = {};
    dispatch(getAllSubEnterpriseAction(subEnterprise, selectedSubEnterprise, false));
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in Get Account: ' + errorMessageDetail,
        severity: 'error',
      })
    );
  }
};

export const createSubEnterPrise = async (dispatch: Function, createAccountPayload: ICreateAccountRequest) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const createAccountResponse = await createNewAccount(createAccountPayload);
    if (createAccountResponse.id) {
      getSubEnterprise(dispatch, { customerId: createAccountPayload.customerId });
    }
    dispatch(hideLoaderAction());
    return true;
  } catch (error: any) {
    let errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in Create Account: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in Create Account: ' + errorMessageDetail,
        severity: 'error',
      })
    );
    return false;
  }
};

export const editSubEnterPrise = async (dispatch: Function, createAccountPayload: ICreateAccountRequest) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const updateAccountResponse = await updateAccount(createAccountPayload);
    if (updateAccountResponse) {
      getSubEnterprise(dispatch, { customerId: createAccountPayload.customerId });
    }
    dispatch(hideLoaderAction());
    return true;
  } catch (error: any) {
    let errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in fetching data: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in Edit Account: ' + errorMessageDetail,
        severity: 'error',
      })
    );
    return false;
  }
};

export const removeSubEnterprise = async (dispatch: Function, deleteSubEnterprisePayload: IDeleteSubEnterpriseRequest) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const updateSubEnterProseResponse = await deleteSubEnterprise(deleteSubEnterprisePayload);
    getSubEnterprise(dispatch, { customerId: deleteSubEnterprisePayload.customerId });
    dispatch(hideLoaderAction());
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in Delete Account: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in Delete Account: ' + errorMessageDetail,
        severity: 'error',
      })
    );
  }
};

export const resetDataOnChangeCustomer = async (dispatch: Function) => {
  dispatch(getAllSubEnterpriseAction([], {}, true));
  dispatch(getAllRealmAction([], {}, {}, true));
};

export const getSubEnterpriseEntitlement = async (dispatch: Function, payload: IGetSubEnterpriseEntitlement) => {
  let subEnterpriseEntitlement: any = [];
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const entitlementsResponse = await getAllEntitlementForSubEnterprise(payload);
    dispatch(setEntitlementsForSubEnterprise(entitlementsResponse));
    dispatch(hideLoaderAction());
    return entitlementsResponse;
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in Get Account Entitlement: ' + errorMessageDetail
    };
    subEnterpriseEntitlement = [];
    dispatch(setEntitlementsForSubEnterprise(subEnterpriseEntitlement));
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in Get Account Entitlement: ' + errorMessageDetail,
        severity: 'error',
      })
    );
    return subEnterpriseEntitlement;
  }
};

export const updateEntitlements = async (dispatch: Function, payload: IUpdateEntitlementRequest) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    await updateEntitlementsForSubEnterprise(payload);
    // getSubEnterpriseEntitlement(dispatch, { id: payload.id, customerId: payload.customerId });
    dispatch(hideLoaderAction());
    return true;
  } catch (error: any) {
    let errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in Update Account Entitlement: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in Update Account Entitlement: ' + errorMessageDetail,
        severity: 'error',
      })
    );
    return false;
  }
}